import { Card } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/Card';
import { Stars } from '@johnlewispartnership/wtr-ingredients/dist/ingredients/StarRating/Stars';
import { Clock } from '@johnlewispartnership/wtr-ingredients/dist/foundations/icons';
import Typography from '@johnlewispartnership/wtr-ingredients/dist/foundations/typography';
import type { RecipeSummaryCard } from 'services/recipes/types';

import { recipeURL, selectImage } from '../utils/formatRecipe';
import styles from './RecipeList.module.scss';

const formatRecipeDuration = (minutesTotal: number) => {
  const hours = Math.floor(minutesTotal / 60);
  const minutes = minutesTotal % 60;

  const formattedHours = hours ? `${hours} hr${hours > 1 ? 's' : ''}` : '';
  const formattedMinutes = minutes ? `${minutes} mins` : '';

  return `${formattedHours}${formattedHours && formattedMinutes ? ' ' : ''}${formattedMinutes}`;
};

const RecipeList = ({ recipes }: { recipes: RecipeSummaryCard[] }) => (
  <ul className={styles.grid}>
    {recipes.map((recipe, index) => {
      const [image] = recipe.images;

      return (
        <li key={recipe.id}>
          <Card
            interactive
            href={recipeURL(recipe)}
            fullWidth
            fullHeight
            className={styles.card}
            image={
              <img
                alt={image?.alternativeText || recipe.id}
                src={selectImage(recipe)}
                aria-hidden="true"
                loading={index > 2 ? 'lazy' : undefined}
              />
            }
            footer={
              <Typography
                noMargins
                styleAs="paragraphSmallLight"
                aria-hidden="true"
                className={styles.duration}
              >
                {recipe.totalTime ? (
                  <>
                    <Clock data-testid="clock-icon" size="xsmall" />
                    {formatRecipeDuration(recipe.totalTime)}
                  </>
                ) : (
                  ' '
                )}
              </Typography>
            }
          >
            <div className={styles.ratingTitle}>
              <Typography
                noMargins
                styleAs="sectionHeading"
                component="h3"
                aria-label={`Recipe: ${recipe.title}`}
                className={styles.title}
              >
                {recipe.title}
              </Typography>

              {recipe.totalRatings > 0 && (
                <div className={styles.ratingSummary} data-testid="rating-summary">
                  <Stars
                    starSize="xsmall"
                    rating={recipe.averageRating!}
                    aria-label={`${recipe.averageRating} out of 5`}
                  />
                  <Typography
                    noMargins
                    element="span"
                    styleAs="paragraphHeading"
                    aria-label={`${recipe.totalRatings} ratings`}
                  >
                    ({recipe.totalRatings})
                  </Typography>
                </div>
              )}
            </div>
          </Card>
        </li>
      );
    })}
  </ul>
);

export default RecipeList;
